import { useEffect } from 'react';

const DURATION = '.8s';
const EASING = 'cubic-bezier(.22,.61,.36,1)';
const STAGGER_MS = 70;
const MAX_STAGGER_MS = 350;

// Collect the blocks worth animating: the direct children of each section's
// content wrapper, or that child's own children when it is a small row/grid
// (so cards stagger in rather than arriving as one slab).
function revealTargets(root) {
  const targets = [];

  for (const section of root.querySelectorAll('section, footer')) {
    const wrap = section.querySelector(':scope > div, :scope > div > div');
    if (!wrap) continue;

    for (const child of wrap.children) {
      if (child.getAttribute('aria-hidden') === 'true') continue;
      const style = getComputedStyle(child);
      if (style.position === 'absolute' || style.position === 'sticky') continue;

      const kids = [...child.children];
      const isGroup =
        (style.display === 'grid' || style.display === 'flex') &&
        kids.length > 1 &&
        kids.length < 9;
      // Marquees animate themselves; staggering their track would fight that.
      const hasMarquee =
        child.querySelector('[style*="bb-marquee"]') ||
        /bb-marquee/.test(child.getAttribute('style') || '');

      if (isGroup && !hasMarquee) {
        kids.forEach((kid, i) => targets.push([kid, Math.min(i * STAGGER_MS, MAX_STAGGER_MS)]));
      } else {
        targets.push([child, 0]);
      }
    }
  }

  return targets;
}

const show = (el) => {
  el.style.opacity = '1';
  el.style.transform = 'translateY(0)';
};

/** Fades sections in as they scroll into view. No-op under reduced motion. */
export function useScrollReveal(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let observer;
    let pending = [];
    let frame;

    // Wait two frames so layout has settled and getComputedStyle reports the
    // final display/position values.
    frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const targets = revealTargets(root);

        for (const [el, delay] of targets) {
          el.style.opacity = '0';
          el.style.transform = 'translateY(16px)';
          el.style.transition = `opacity ${DURATION} ${EASING} ${delay}ms, transform ${DURATION} ${EASING} ${delay}ms`;
          el.style.willChange = 'opacity, transform';
        }

        observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              show(entry.target);
              observer.unobserve(entry.target);
              setTimeout(() => {
                entry.target.style.willChange = '';
              }, 1200);
            }
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.06 },
        );

        pending = targets.map(([el]) => el);
        for (const el of pending) observer.observe(el);
        revealRest();
      });
    });

    // Anything still hidden once the page is scrolled to the bottom can never
    // trigger the observer, so reveal it outright.
    function revealRest() {
      if (!pending.length) return;
      const doc = document.documentElement;
      const atBottom =
        window.innerHeight + (window.scrollY || doc.scrollTop || 0) >= (doc.scrollHeight || 0) - 8;
      if (!atBottom) return;

      for (const el of pending) {
        if (el.style.opacity === '1') continue;
        show(el);
        observer?.unobserve(el);
      }
      pending = [];
    }

    const onScroll = () => revealRest();
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('scroll', onScroll);
    };
  }, [rootRef]);
}
